import type { components, operations } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { api } from '@/lib/api'
import type { FiscalPeriodType } from './period-type'

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time.
 *
 * TanStack Query hooks capture their closure at render; a screen whose `me`
 * query resolves after first render would otherwise send the mutation with an
 * empty X-Company-Id. The ref holds the latest companyId between renders.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

export type FiscalPeriod = components['schemas']['FiscalPeriodResource']

type FiscalPeriodIndexBody =
  operations['fiscalPeriod.index']['responses'][200]['content']['application/json']

export type FiscalYearWithPeriods = FiscalPeriodIndexBody['data'][number]

export function useFiscalYears(
  companyId: string | null,
  options?: Omit<UseQueryOptions<FiscalYearWithPeriods[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FiscalYearWithPeriods[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'fiscal-periods'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<FiscalYearWithPeriods[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/fiscal-periods', {
        headers: { 'X-Company-Id': companyId },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load periods')
      return unwrap<FiscalYearWithPeriods[]>(response.data ?? {}, 'fiscal-periods') ?? []
    },
  })
}

/**
 * Lock or unlock one fiscal period (FR-ACC-082, 083).
 *
 * The period screen listed the periods and their status but offered no
 * way to change one, even though all three endpoints existed — a register you
 * can read and not act on. The API authorises and audits every period write;
 * this only reaches it.
 */
export function useSetPeriodLock(
  companyId: string | null,
  options?: Omit<
    UseMutationOptions<void, Error, { periodId: string; lock: boolean }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)

  return useMutation<void, Error, { periodId: string; lock: boolean }>({
    ...options,
    mutationFn: async ({ periodId, lock }) => {
      const path = lock
        ? '/accounting/fiscal-periods/{period}/lock'
        : '/accounting/fiscal-periods/{period}/unlock'
      const response = await api.POST(path, {
        headers: { 'X-Company-Id': currentCompany() },
        params: { path: { period: periodId } },
      })
      if (response.error) {
        throw new Error(response.error.message ?? 'Failed to change the period lock')
      }
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting', 'fiscal-periods'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Open a fiscal year with twelve monthly periods or four fiscal quarters
 * (FR-ACC-080..083, f287).
 *
 * `POST /accounting/fiscal-years` has existed since Wave 1 and the screen even
 * carried a `periods.generateYear` translation, but nothing rendered a control
 * — so a company reaching the end of its seeded year had no way to open the
 * next one, and every posting after 31 December would have failed with no
 * period to land in.
 */
export function useGenerateFiscalYear(
  companyId: string | null,
  options?: Omit<UseMutationOptions<unknown, Error, GenerateFiscalYearInput>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<unknown, Error, GenerateFiscalYearInput>({
    ...options,
    mutationFn: async ({ year, periodType }) => {
      const response = await api.POST('/accounting/fiscal-years', {
        headers: { 'X-Company-Id': currentCompany() },
        body: { year, period_type: periodType },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to generate the year')
      return response.data
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'accounting'] })
      options?.onSuccess?.(...args)
    },
  })
}

export type GenerateFiscalYearInput = { year: number; periodType: FiscalPeriodType }
